import React from "react";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { checkIsActive, KTSVG, WithChildren } from "../../../helpers";
import { useLayout } from "../../core";

type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
};

/**
 * Walk the children tree and collect every `to` prop we find on descendant
 * menu items. Used to keep the parent accordion expanded when any child route
 * matches the current pathname.
 */
function collectChildPaths(node: React.ReactNode, acc: string[] = []): string[] {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    const childTo = (child.props as { to?: string })?.to;
    if (typeof childTo === "string" && childTo) {
      // Strip query string so `checkIsActive` can match on the path portion.
      acc.push(childTo.split("?")[0]);
    }
    const grandChildren = (child.props as { children?: React.ReactNode })?.children;
    if (grandChildren) collectChildPaths(grandChildren, acc);
  });
  return acc;
}

const AsideMenuItemWithSub: React.FC<Props & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet,
}) => {
  const { pathname } = useLocation();
  const childPaths = collectChildPaths(children);
  const isActive =
    checkIsActive(pathname, to) ||
    childPaths.some((p) => checkIsActive(pathname, p));
  const { config } = useLayout();
  const { aside } = config;

  return (
    <div
      className={clsx("menu-item", { "here show": isActive }, "menu-accordion")}
      data-kt-menu-trigger="click"
    >
      <span className="menu-link">
        {hasBullet && (
          <span className="menu-bullet">
            <span className="bullet bullet-dot"></span>
          </span>
        )}
        {icon && aside.menuIcon === "svg" && (
          <span className="menu-icon">
            <KTSVG path={icon} className="svg-icon-2" />
          </span>
        )}
        {fontIcon && aside.menuIcon === "font" && (
          <i className={clsx("bi fs-3", fontIcon)}></i>
        )}
        <span className="menu-title">{title}</span>
        <span className="menu-arrow"></span>
      </span>
      <div
        className={clsx("menu-sub menu-sub-accordion", {
          "menu-active-bg": isActive,
        })}
      >
        {children}
      </div>
    </div>
  );
};

export { AsideMenuItemWithSub };
